import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Container,
  Divider,
  Stack,
  Typography,
} from "@mui/material";
import {
  ArrowForward as ArrowForwardIcon,
  Bloodtype as BloodDonationIcon,
  Business as BusinessIcon,
  Event as EventIcon,
  Work as WorkIcon,
} from "@mui/icons-material";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import api from "../api/api";

const featureSections = [
  {
    id: "entrepreneurship",
    path: "/entrepreneurs",
    title: "Entrepreneurship Opportunities",
    description:
      "Showcase your business to the Einstein Alumni community. Share your business details so fellow alumni can discover trusted products and services, explore partnership opportunities, support alumni-owned businesses, and benefit from exclusive community offers.",
    image: "/images/enterprenur_opp.png",
    alt: "Entrepreneurship opportunities",
    icon: BusinessIcon,
    iconBg: "#eff6ff",
    iconColor: "#2563eb",
    reverse: false,
  },
  {
    id: "job-referrals",
    path: "/job-referral",
    title: "Job Referrals",
    description:
      "The Alumni Career Network brings opportunities and talent together. Alumni can share job openings, employee referrals, internships, and recruitment drives, while job seekers can showcase their profiles and career aspirations. Connect with trusted professionals, expand your network, and help fellow alumni grow through meaningful career opportunities.",
    image: "/images/job_refferal.jpg",
    alt: "Job referral opportunities",
    icon: WorkIcon,
    iconBg: "#ecfdf5",
    iconColor: "#059669",
    reverse: true,
  },
  {
    id: "blood-donation",
    path: "/blood-donation",
    title: "Blood Donation",
    description:
      "Be a lifesaver for the Einstein Alumni community. Register as a voluntary blood donor or share urgent blood donation requests to help fellow alumni, their families, and those in need during medical emergencies. Together, we can ensure timely support when every drop counts.",
    image: "/images/bllod_donation.JPG",
    alt: "Blood donation awareness",
    icon: BloodDonationIcon,
    iconBg: "#fef2f2",
    iconColor: "#dc2626",
    reverse: false,
  },
];

function normalizeHomeEvent(event) {
  const eventDate = event?.EventDate || event?.eventDate || event?.date || "";
  const startTime = event?.StartTime || event?.startTime || "";

  return {
    id: event?.EventId || event?.eventId || event?.id || event?.Id,
    title:
      event?.EventTitle ||
      event?.eventTitle ||
      event?.title ||
      "Untitled Event",
    date: eventDate,
    displayDate:
      eventDate && startTime
        ? `${new Date(eventDate).toLocaleDateString("en-IN", {
            day: "numeric",
            month: "short",
            year: "numeric",
          })} • ${startTime}`
        : eventDate
          ? new Date(eventDate).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })
          : "Date to be announced",
    description:
      event?.EventDescription ||
      event?.eventDescription ||
      event?.description ||
      "Event details will be published soon.",
  };
}

function hasValidDate(event) {
  return !Number.isNaN(new Date(event.date).getTime());
}

function HeroVideo() {
  return (
    <Box
      sx={{
        position: "relative",
        height: { xs: 280, md: 380 },
        overflow: "hidden",
        borderRadius: 4,
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.14)",
      }}
    >
      <Box
        component="video"
        src="/video/logo.mp4"
        autoPlay
        muted
        loop
        playsInline
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          display: "block",
        }}
      />
    </Box>
  );
}

function FeatureSection({
  id,
  path,
  title,
  description,
  image,
  alt,
  icon: Icon,
  iconBg,
  iconColor,
  reverse = false,
  onExplore,
}) {
  return (
    <Card
      id={id}
      sx={{
        borderRadius: 4,
        p: { xs: 2, md: 3 },
        boxShadow: "0 16px 40px rgba(15, 23, 42, 0.08)",
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: reverse ? "1.15fr 0.85fr" : "0.85fr 1.15fr",
          },
          gap: 3,
          alignItems: "center",
        }}
      >
        <Box sx={{ order: { xs: 0, md: reverse ? 2 : 1 } }}>
          <Box
            component="img"
            src={image}
            alt={alt}
            sx={{
              width: "100%",
              height: 240,
              objectFit: "cover",
              borderRadius: 3,
              boxShadow: "0 10px 28px rgba(15, 23, 42, 0.1)",
            }}
          />
        </Box>
        <Box sx={{ order: { xs: 0, md: reverse ? 1 : 2 } }}>
          <Box
            sx={{
              display: "inline-flex",
              p: 1.5,
              borderRadius: "50%",
              bgcolor: iconBg,
              mb: 2,
            }}
          >
            <Icon sx={{ fontSize: 32, color: iconColor }} />
          </Box>
          <Typography
            variant="h5"
            fontWeight={700}
            color="#0f172a"
            sx={{ mb: 1 }}
          >
            {title}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {description}
          </Typography>
          <Button
            onClick={() => onExplore(path)}
            endIcon={<ArrowForwardIcon />}
            sx={{
              mt: 2,
              px: 0,
              color: iconColor,
              fontWeight: 700,
              textTransform: "none",
              justifyContent: "flex-start",
              "&:hover": { bgcolor: "transparent", color: "#0f172a" },
            }}
          >
            Explore this section
          </Button>
        </Box>
      </Box>
    </Card>
  );
}

function Home() {
  const { user } = useSelector((state) => state.user);
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const currentUserToken =
    user?.token || user?.accessToken || user?.jwtToken || "";

  useEffect(() => {
    let isMounted = true;

    async function fetchHomeEvents() {
      try {
        const response = await api.getEvents(currentUserToken);
        const eventRows = Array.isArray(response?.data?.events)
          ? response.data.events
          : Array.isArray(response?.data?.data?.events)
            ? response.data.data.events
            : Array.isArray(response?.data)
              ? response.data
              : [];

        if (!isMounted) {
          return;
        }

        setEvents(
          eventRows
            .map(normalizeHomeEvent)
            .filter(hasValidDate)
            .sort((left, right) => new Date(right.date) - new Date(left.date))
            .slice(0, 3),
        );
      } catch {
        if (isMounted) {
          setEvents([]);
        }
      }
    }

    fetchHomeEvents();

    return () => {
      isMounted = false;
    };
  }, [currentUserToken]);

  const scrollToSection = (sectionId) => {
    document
      .getElementById(sectionId)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const navigateToLogin = () => {
    navigate("/login");
  };

  const handleExploreEvents = () => {
    if (!user?.email) {
      navigateToLogin();
      return;
    }

    scrollToSection("upcoming-events");
  };

  const handleShareOpportunity = () => {
    if (!user?.email) {
      navigateToLogin();
      return;
    }

    scrollToSection("entrepreneurship");
  };

  const handleExploreFeature = (path) => {
    if (!user?.email) {
      navigateToLogin();
      return;
    }

    navigate(path);
  };

  return (
    <Box sx={{ bgcolor: "#f5f8ff", py: { xs: 4, md: 8 } }}>
      <Container maxWidth="xl">
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.2fr 0.8fr" },
            gap: 4,
            alignItems: "center",
          }}
        >
          <Box>
            <Stack spacing={2.5}>
              <Chip
                label="Alumni Community"
                sx={{
                  width: "fit-content",
                  bgcolor: "#dbeafe",
                  color: "#1d4ed8",
                  fontWeight: 700,
                }}
              />
              <Typography variant="h3" fontWeight={800} color="#0f172a">
                Welcome back to the Einstein Seeds alumni network.
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 680 }}
              >
                Reconnect with the Einstein family, relive cherished memories,
                build lasting relationships, and create new opportunities
                through alumni events, entrepreneurship, mentorship, and career
                referrals
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleExploreEvents}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ borderRadius: 999 }}
                >
                  Explore Events
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleShareOpportunity}
                  endIcon={<ArrowForwardIcon />}
                  sx={{ borderRadius: 999 }}
                >
                  Share Your Opportunity
                </Button>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {user?.email
                  ? `Signed in as ${user.email}`
                  : "Join the community to access exclusive alumni opportunities."}
              </Typography>
            </Stack>
          </Box>

          <Box>
            <HeroVideo />
          </Box>
        </Box>

        <Box id="upcoming-events" sx={{ mt: { xs: 5, md: 7 } }}>
          <Typography
            variant="h4"
            fontWeight={700}
            color="#0f172a"
            sx={{ mb: 3 }}
          >
            Upcoming Events
          </Typography>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: 3,
            }}
          >
            {events.map((event, index) => (
              <Box key={event.id || `${event.title}-${index}`}>
                <Card
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
                  }}
                >
                  <CardContent>
                    <Stack
                      direction="row"
                      spacing={1.5}
                      sx={{ mb: 1.5, alignItems: "center" }}
                    >
                      <EventIcon color="primary" />
                      <Typography variant="subtitle1" fontWeight={700}>
                        {event.title}
                      </Typography>
                    </Stack>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 1.5 }}
                    >
                      {event.displayDate}
                    </Typography>
                    <Typography variant="body1">{event.description}</Typography>
                  </CardContent>
                </Card>
              </Box>
            ))}
            {events.length === 0 ? (
              <Card
                sx={{
                  gridColumn: { xs: "auto", md: "1 / -1" },
                  borderRadius: 3,
                  boxShadow: "0 12px 32px rgba(15, 23, 42, 0.08)",
                }}
              >
                <CardContent>
                  <Typography variant="body1" color="text.secondary">
                    No events are available right now.
                  </Typography>
                </CardContent>
              </Card>
            ) : null}
          </Box>
        </Box>

        <Stack spacing={3} sx={{ mt: { xs: 5, md: 7 } }}>
          {featureSections.map((section) => (
            <FeatureSection
              key={section.id}
              {...section}
              onExplore={handleExploreFeature}
            />
          ))}
        </Stack>

        <Divider sx={{ mt: 6, borderColor: "rgba(15, 23, 42, 0.08)" }} />
      </Container>
    </Box>
  );
}

export default Home;
